import { isEqual } from 'lodash';
import {
  IConsumptionInfo,
  IScenarioPayload,
  ISimulationPayload,
  IUnitPresetPayload,
} from '@schemas/scenarios-simulator/types';
import { getRequiredHistoryFields } from '@schemas/scenarios-simulator/validations/helpers/tariff-helpers';
import { getConsumptionTypeFromTariff } from '@utils/scenarios-simulator/consumption-type';

type ConsumptionHistoryField = 'consumptionLowVoltage' | 'consumptionPeak' | 'consumptionOffPeak';

type PresetPayloadRecord = Record<string, unknown> & {
  tariffGroup?: unknown;
  consumptionInfo?: Record<string, unknown> | null;
};

export const CONSUMPTION_HISTORY_SECTIONS: Array<{
  field: ConsumptionHistoryField;
  visibility: 'showConsumption' | 'showPeakConsumption' | 'showOffPeakConsumption';
  historyKey: string;
}> = [
  { field: 'consumptionLowVoltage', visibility: 'showConsumption', historyKey: 'ConsumptionHistory' },
  { field: 'consumptionPeak', visibility: 'showPeakConsumption', historyKey: 'PeakConsumptionHistory' },
  { field: 'consumptionOffPeak', visibility: 'showOffPeakConsumption', historyKey: 'OffPeakConsumptionHistory' },
];

export const customHistoryFlagOf = (historyKey: string): string => `aux__useCustomHistory_${historyKey}`;

const isFlagEnabled = (value: unknown): boolean => value === true || value === 'true';

const hasMonthValues = (history: unknown): history is Record<string, unknown> =>
  Boolean(history) &&
  typeof history === 'object' &&
  Object.keys(history as Record<string, unknown>).some((key) => key.startsWith('month'));

const presetOf = (unit: IUnitPresetPayload | undefined): PresetPayloadRecord | undefined =>
  unit?.presetPayload as PresetPayloadRecord | undefined;

const sharesConsumptionType = (basePreset: PresetPayloadRecord, targetPreset: PresetPayloadRecord): boolean => {
  const baseConsumptionType = getConsumptionTypeFromTariff(basePreset.tariffGroup);
  return Boolean(baseConsumptionType) && baseConsumptionType === getConsumptionTypeFromTariff(targetPreset.tariffGroup);
};

const isInheritingUnit = (targetUnit: IUnitPresetPayload | undefined): boolean =>
  Boolean(targetUnit?.presetType) && targetUnit?.presetType !== 'undefined';

export function getInheritedConsumptionHistory(
  baseUnit: IUnitPresetPayload | undefined,
  targetUnit: IUnitPresetPayload | undefined,
): Partial<IConsumptionInfo> {
  const basePreset = presetOf(baseUnit);
  const targetPreset = presetOf(targetUnit);

  if (
    !basePreset ||
    !targetPreset ||
    !isInheritingUnit(targetUnit) ||
    !sharesConsumptionType(basePreset, targetPreset)
  ) {
    return {};
  }

  const visibleHistory = getRequiredHistoryFields(targetPreset.tariffGroup);

  return CONSUMPTION_HISTORY_SECTIONS.reduce<Partial<IConsumptionInfo>>(
    (inherited, { field, visibility, historyKey }) => {
      if (!visibleHistory[visibility] || isFlagEnabled(targetPreset[customHistoryFlagOf(historyKey)])) {
        return inherited;
      }

      const baseHistory = basePreset.consumptionInfo?.[field];
      if (!hasMonthValues(baseHistory) || isEqual(baseHistory, targetPreset.consumptionInfo?.[field])) {
        return inherited;
      }

      return { ...inherited, [field]: { ...baseHistory } as IConsumptionInfo[ConsumptionHistoryField] };
    },
    {},
  );
}

export interface InheritedConsumptionHistoryUpdate {
  scenarioIndex: number;
  unitIndex: number;
  history: Partial<IConsumptionInfo>;
}

export function collectInheritedConsumptionHistory(
  baseScenario?: IScenarioPayload | null,
  targetScenarios?: IScenarioPayload[] | null,
): InheritedConsumptionHistoryUpdate[] {
  const baseUnits = baseScenario?.scenarioPayload;
  if (!baseUnits || !targetScenarios) {
    return [];
  }

  return targetScenarios.flatMap((targetScenario, scenarioIndex) =>
    (targetScenario?.scenarioPayload ?? []).reduce<InheritedConsumptionHistoryUpdate[]>(
      (updates, targetUnit, unitIndex) => {
        const history = getInheritedConsumptionHistory(baseUnits[unitIndex], targetUnit);
        return Object.keys(history).length === 0 ? updates : [...updates, { scenarioIndex, unitIndex, history }];
      },
      [],
    ),
  );
}

export function syncTargetsConsumptionHistoryToBase(
  baseScenario?: IScenarioPayload | null,
  targetScenarios?: IScenarioPayload[] | null,
): IScenarioPayload[] {
  if (!targetScenarios) {
    return [];
  }

  const updates = collectInheritedConsumptionHistory(baseScenario, targetScenarios);
  if (updates.length === 0) {
    return targetScenarios;
  }

  return targetScenarios.map((targetScenario, scenarioIndex) => {
    const scenarioUpdates = updates.filter((update) => update.scenarioIndex === scenarioIndex);
    if (scenarioUpdates.length === 0 || !targetScenario?.scenarioPayload) {
      return targetScenario;
    }

    const scenarioPayload = targetScenario.scenarioPayload.map((targetUnit, unitIndex) => {
      const update = scenarioUpdates.find((candidate) => candidate.unitIndex === unitIndex);
      if (!update) {
        return targetUnit;
      }

      return {
        ...targetUnit,
        presetPayload: {
          ...targetUnit.presetPayload,
          consumptionInfo: {
            ...(targetUnit.presetPayload?.consumptionInfo ?? {}),
            ...update.history,
          },
        },
      } as IUnitPresetPayload;
    });

    return { ...targetScenario, scenarioPayload };
  });
}

export function inheritBaseConsumptionHistoryInTargets(payload: ISimulationPayload): ISimulationPayload {
  const parsedPayload = payload?.inputs?.parsedPayload;
  const targetScenarios = parsedPayload?.targetScenarios;

  if (!parsedPayload?.baseScenario || !targetScenarios || targetScenarios.length === 0) {
    return payload;
  }

  const syncedTargetScenarios = syncTargetsConsumptionHistoryToBase(parsedPayload.baseScenario, targetScenarios);
  if (syncedTargetScenarios.every((scenario, index) => scenario === targetScenarios[index])) {
    return payload;
  }

  return {
    ...payload,
    inputs: {
      ...payload.inputs,
      parsedPayload: {
        ...parsedPayload,
        targetScenarios: syncedTargetScenarios,
      },
    },
  };
}

export interface ClearableCustomHistoryFlag {
  scenarioIndex: number;
  unitIndex: number;
  flag: string;
}

export function collectRedundantCustomHistoryFlags(
  baseScenario?: IScenarioPayload | null,
  targetScenarios?: IScenarioPayload[] | null,
): ClearableCustomHistoryFlag[] {
  const baseUnits = baseScenario?.scenarioPayload;
  if (!baseUnits || !targetScenarios) {
    return [];
  }

  return targetScenarios.flatMap((targetScenario, scenarioIndex) =>
    (targetScenario?.scenarioPayload ?? []).flatMap((targetUnit, unitIndex) => {
      const basePreset = presetOf(baseUnits[unitIndex]);
      const targetPreset = presetOf(targetUnit);

      if (!basePreset || !targetPreset || !sharesConsumptionType(basePreset, targetPreset)) {
        return [];
      }

      return CONSUMPTION_HISTORY_SECTIONS.filter(({ field, historyKey }) => {
        const targetHistory = targetPreset.consumptionInfo?.[field];
        return (
          isFlagEnabled(targetPreset[customHistoryFlagOf(historyKey)]) &&
          hasMonthValues(targetHistory) &&
          isEqual(targetHistory, basePreset.consumptionInfo?.[field])
        );
      }).map(({ historyKey }) => ({ scenarioIndex, unitIndex, flag: customHistoryFlagOf(historyKey) }));
    }),
  );
}
